import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { Calendar, ChevronLeft, ChevronRight, X, Zap, CheckCircle2, FileText } from 'lucide-react';

import PatternTool from './PatternTool';
import ScheduleGrid from './ScheduleGrid';
import ShiftEditDrawer from './ShiftEditDrawer';
import { shiftTypesForDuty } from '../constants/shifts';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

export interface StaffMember {
  id: number;
  full_name?: string;
  name?: string;
  first_name?: string;
  monthly_salary?: number | string;
  staff_type?: string;
}
// Keep type alias so other files that import Nurse don't break
export type Nurse = StaffMember;

export interface Patient {
  id: number;
  patient_name: string;
  age?: number | string;
  sex?: string;
  phone_number?: string;
  address?: string;
  status?: string;
  on_assignment_board?: boolean;  // Operations Dashboard membership (independent of status)
  duty_type?: string;
  duty_type_specify?: string;
  photo_url?: string;
  case_start_date?: string;
  location_link?: string;
  case_manager?: string;
  patient_clients?: any[];
  clients_info?: any[];
  client_name?: string;
  treating_details?: { doctor?: string; hospital?: string; department?: string }[];
}

interface SelectedSlot { date: string; shiftId: string; session: string; }
interface ShiftColumn { id: string; label: string; type: string; }

interface AssignNurseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  patient: Patient | null;
  nurses: StaffMember[];
  physios: StaffMember[];
  selectedDate: string;
  initialSlots?: SelectedSlot[];
  preNurseId?: string;
  // nurse_id -> unavailable category (Emergency Leave / Staff On Sick / Planned Leave / On Temporary Duty)
  blockedStaff?: Record<number, string>;
  // called after assigning staff who were unavailable, so the parent can flip their availability
  onOverrideAvailability?: (nurseIds: number[]) => void;
}

const getClientName = (patient: any) => {
  if (!patient) return 'Direct Client';
  if (patient.client_name) return patient.client_name;
  const list = patient.patient_clients || patient.clients_info || [];
  if (list.length > 0) return list[0].client_name || list[0].name || 'Direct Client';
  return 'Direct Client';
};

// --- QUICK ADD NURSE MODAL ---
const AddPhysioModal: React.FC<{ isOpen: boolean; onClose: () => void; onSuccess: () => void }> = ({ isOpen, onClose, onSuccess }) => {
  const [name, setName] = useState('');
  const [salary, setSalary] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  if (!isOpen) return null;
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('accessToken');
      const fd = new FormData();
      fd.append("physio_data", JSON.stringify({
        full_name: name, monthly_salary: parseFloat(salary) || 0,
        mobile_numbers: [], whatsapp_number: [], emails: [], education_rows: []
      }));
      await axios.post(`${API_BASE_URL}/api/physiotherapists/`, fd, { headers: { 'Authorization': `Bearer ${token}` } });
      onSuccess();
    } catch (err) { console.error(err); } finally { setIsSubmitting(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 6000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '12px', width: '400px' }}>
        <h3 style={{ marginTop: 0 }}>Quick Add Physiotherapist</h3>
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '5px' }}>Full Name</label>
          <input style={{ width: '100%', padding: '8px', boxSizing: 'border-box', border: '1px solid #cbd5e1', borderRadius: '6px' }} value={name} onChange={e => setName(e.target.value)} />
        </div>
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '5px' }}>Monthly Salary</label>
          <input style={{ width: '100%', padding: '8px', boxSizing: 'border-box', border: '1px solid #cbd5e1', borderRadius: '6px' }} type="number" value={salary} onChange={e => setSalary(e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={onClose} style={{ flex: 1, padding: '10px', background: '#f1f5f9', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleSubmit} disabled={isSubmitting} style={{ flex: 1, padding: '10px', backgroundColor: '#7c3aed', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Save</button>
        </div>
      </div>
    </div>
  );
};

const AddNurseModal: React.FC<{ isOpen: boolean; onClose: () => void; onSuccess: () => void }> = ({ isOpen, onClose, onSuccess }) => {
  const [name, setName] = useState('');
  const [salary, setSalary] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  if (!isOpen) return null;
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('accessToken');
      const fd = new FormData();
      fd.append("nurse_data", JSON.stringify({
        full_name: name, name, monthly_salary: parseFloat(salary) || 0,
        dob: "1900-01-01", sex: "Other", status: 'active',
        mobile_numbers: [], whatsapp_number: [], emails: [], qualifications: [],
        registrations: [], hospitals: [], languages: [], emergency_contacts: []
      }));
      await axios.post(`${API_BASE_URL}/api/nurses/`, fd, { headers: { 'Authorization': `Bearer ${token}` } });
      onSuccess();
    } catch (err) { console.error(err); } finally { setIsSubmitting(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 6000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '12px', width: '400px' }}>
        <h3 style={{ marginTop: 0 }}>Quick Add Nurse</h3>
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '5px' }}>Full Name</label>
          <input style={{ width: '100%', padding: '8px', boxSizing: 'border-box', border: '1px solid #cbd5e1', borderRadius: '6px' }} value={name} onChange={e => setName(e.target.value)} />
        </div>
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '5px' }}>Monthly Salary</label>
          <input style={{ width: '100%', padding: '8px', boxSizing: 'border-box', border: '1px solid #cbd5e1', borderRadius: '6px' }} type="number" value={salary} onChange={e => setSalary(e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={onClose} style={{ flex: 1, padding: '10px', background: '#f1f5f9', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleSubmit} disabled={isSubmitting} style={{ flex: 1, padding: '10px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Save</button>
        </div>
      </div>
    </div>
  );
};

const calculateHoursDifference = (inTime: string, outTime: string): number => {
  if (!inTime || !outTime) return 0;
  const [inH, inM] = inTime.split(':').map(Number);
  const [outH, outM] = outTime.split(':').map(Number);
  let diff = outH + outM / 60 - (inH + inM / 60);
  if (diff < 0) diff += 24;
  if (diff === 0) diff = 24;
  return Math.round(diff * 100) / 100;
};

const getStandardHours = (shiftType: string): number => {
  const lower = (shiftType || '').toLowerCase();
  if (lower.includes('24')) return 24;
  if (lower.includes('12')) return 12;
  return 8;
};

// Resigned / Absconded / Uncertainty are a HARD stop — no "assign anyway"
// override, unlike leave / on-temporary-duty which just warn. Matches
// app/api/nurse_availability.py PERMANENT_STATUSES.
const PERMANENT_AVAIL_STATUSES = ['Resigned', 'Absconded', 'Uncertainty'];

const AssignNurseModal: React.FC<AssignNurseModalProps> = ({ isOpen, onClose, onSuccess, patient, nurses, physios, selectedDate, initialSlots, preNurseId, blockedStaff, onOverrideAvailability }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date(selectedDate || Date.now()));
  // A simple, in-app "can't assign" dialog — not the browser's native alert —
  // shown for hard-blocked (permanent-status) staff. Just an OK button; no
  // override is offered.
  const [blockedError, setBlockedError] = useState<string | null>(null);
  const [scheduleMap, setScheduleMap] = useState<Record<string, any>>({});
  // Change-alert flags for this patient's month → set of `${date}|${shift_type}`.
  const [flaggedCells, setFlaggedCells] = useState<Set<string>>(new Set());
  const [selectedSlots, setSelectedSlots] = useState<SelectedSlot[]>([]);
  const [isAddNurseModalOpen, setIsAddNurseModalOpen] = useState(false);
  const [isAddPhysioModalOpen, setIsAddPhysioModalOpen] = useState(false);
  const [localNurses, setLocalNurses] = useState(nurses);
  const [localPhysios, setLocalPhysios] = useState(physios);
  const [shifts, setShifts] = useState<ShiftColumn[]>([]);
  // Schedule note (per-patient, persisted on patients.schedule_note) — separate
  // from the ops-board card note (ops_note).
  const [noteVal, setNoteVal] = useState('');
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);

  useEffect(() => { setLocalNurses(nurses); }, [nurses]);
  useEffect(() => { setLocalPhysios(physios); }, [physios]);
  // Load this patient's schedule note whenever the modal (re)opens.
  useEffect(() => { if (isOpen) setNoteVal(((patient as any)?.schedule_note as string) || ''); }, [isOpen, patient]);

  const openNote = () => { setNoteDraft(noteVal); setNoteOpen(true); };
  const saveNote = async () => {
    if (!patient) return;
    const note = noteDraft.trim();
    setNoteSaving(true);
    try {
      const token = localStorage.getItem('accessToken');
      const headers = { 'Authorization': `Bearer ${token}` };
      const current = (await axios.get(`${API_BASE_URL}/api/patients/${patient.id}`, { headers })).data;
      const fd = new FormData();
      fd.append('patient_data', JSON.stringify({
        ...current, schedule_note: note,
        clients_info: (current.patient_clients || []).map((c: any) => ({
          id: c.id, name: c.client_name, relationship: c.relationship,
          contact_number: c.contact_number_1, secondary_contact_number: c.contact_number_2, email: c.email,
        })),
      }));
      await axios.patch(`${API_BASE_URL}/api/patients/${patient.id}`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      setNoteVal(note);
      setNoteOpen(false);
    } catch (e) { console.error('Failed to save schedule note', e); alert('Failed to save note.'); }
    finally { setNoteSaving(false); }
  };

  // Auto-select slots when opened from the daily board (nurse pill / assign button click)
  useEffect(() => {
    if (isOpen && initialSlots && initialSlots.length > 0) {
      setSelectedSlots(initialSlots);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshNurses = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const res = await axios.get(`${API_BASE_URL}/api/nurses/`, { headers: { 'Authorization': `Bearer ${token}` } });
      setLocalNurses((res.data as StaffMember[]).map(n => ({ ...n, staff_type: 'nurse' })));
    } catch (e) { console.error(e); }
  };

  const refreshPhysios = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const res = await axios.get(`${API_BASE_URL}/api/physiotherapists/`, { headers: { 'Authorization': `Bearer ${token}` } });
      setLocalPhysios((res.data as StaffMember[]).map(p => ({ ...p, staff_type: 'physio' })));
    } catch (e) { console.error(e); }
  };

  const [showQuickFill, setShowQuickFill] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const monthLabel = currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  /** Look up a staff member by nurse_id or physio_id */
  const resolveByNurseId  = useCallback((id: number): StaffMember | undefined => localNurses.find(n => n.id === id),  [localNurses]);
  const resolveByPhysioId = useCallback((id: number): StaffMember | undefined => localPhysios.find(p => p.id === id), [localPhysios]);

  const loadScheduleData = useCallback(async () => {
    if (!patient) return;
    setIsFetching(true);
    const token = localStorage.getItem('accessToken');
    const headers = { 'Authorization': `Bearer ${token}` };
    try {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth();
      const start = `${year}-${String(month + 1).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month + 1, 0).getDate();
      const end = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

      // NEW two-layer flow: read schedule_shifts (one row per date/shift/role)
      const [schedRes, prefRes, flagRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/schedule/${patient.id}`, { params: { start, end }, headers }),
        axios.get(`${API_BASE_URL}/api/patients/${patient.id}/shift-preferences`, { headers }).catch(() => ({ data: { shift_order: [] } })),
        axios.get(`${API_BASE_URL}/api/schedule/flags`, { params: { start, end }, headers }).catch(() => ({ data: [] })),
      ]);

      // Yellow cells in the grid reflect ACTUAL assignment changes only
      // (auto_changed). The manual 🚩 flag on the ops card is a separate marker
      // and intentionally does NOT paint the grid.
      setFlaggedCells(new Set(
        (Array.isArray(flagRes.data) ? flagRes.data : [])
          .filter((f: any) => f.patient_id === patient.id && f.auto_changed)
          .map((f: any) => `${String(f.date).split('T')[0]}|${f.shift_type}`)
      ));

      const rows: any[] = Array.isArray(schedRes.data) ? schedRes.data : [];
      const savedOrder = prefRes.data?.shift_order || [];

      // --- BUILD COLUMNS ---
      let finalShifts: ShiftColumn[] = [];
      if (savedOrder.length > 0 && typeof savedOrder[0] === 'object') {
        finalShifts = [...savedOrder];
      } else {
        // Canonical duty → shift columns (constants/shifts.ts) — fixes 12 Day/12 Night/
        // Physiotherapy defaulting to three 8-hour columns.
        const defaultTypes = shiftTypesForDuty(patient.duty_type);
        finalShifts = defaultTypes.map(type => ({ id: type.toLowerCase().replace(/\s+/g, '_'), label: type, type }));
      }
      const activeTypes = new Set<string>(finalShifts.map(s => s.type));
      const ensureShiftColumn = (type: string) => {
        if (type && !activeTypes.has(type)) {
          activeTypes.add(type);
          finalShifts.push({ id: `${type.toLowerCase().replace(/\s+/g, '_')}_${Date.now()}`, label: type, type });
        }
      };
      if (!(savedOrder.length > 0 && typeof savedOrder[0] === 'object')) {
        rows.forEach((r: any) => ensureShiftColumn(r.shift_type));
      }
      setShifts(finalShifts);

      // --- GROUP rows by date -> shift_type -> role ---
      const staffNameOf = (row: any) => {
        if (!row) return '';
        if (row.nurse?.full_name) return row.nurse.full_name;
        if (row.physio?.full_name) return row.physio.full_name;
        if (row.nurse_id) { const n = resolveByNurseId(row.nurse_id); return n?.full_name || n?.name || 'Unknown'; }
        if (row.physio_id) { const p = resolveByPhysioId(row.physio_id); return p?.full_name || p?.name || 'Unknown'; }
        return 'Unknown';
      };

      const grouped: Record<string, Record<string, { primary?: any; assistant?: any }>> = {};
      rows.forEach((r: any) => {
        const d = String(r.date).split('T')[0];
        if (!grouped[d]) grouped[d] = {};
        if (!grouped[d][r.shift_type]) grouped[d][r.shift_type] = {};
        grouped[d][r.shift_type][r.role === 'assistant' ? 'assistant' : 'primary'] = r;
      });

      const newSchedule: Record<string, any> = {};
      Object.keys(grouped).forEach(d => {
        newSchedule[d] = {};
        Object.keys(grouped[d]).forEach(st => {
          const p = grouped[d][st].primary;
          const a = grouped[d][st].assistant;
          if (!p && !a) return;
          newSchedule[d][st] = {
            assignment_id: p?.id || a?.id,
            nurse: staffNameOf(p),
            nurse_id: p?.nurse_id || null,
            physio_id: p?.physio_id || null,
            rate: p?.rate ?? null,
            monthly_scale: p?.monthly_scale ?? null,
            total_charge: p?.client_charge ?? null,
            in_time: p?.in_time || null,
            out_time: p?.out_time || null,
            hasAssistant: !!a,
            assistant: staffNameOf(a),
            second_nurse_id: a?.nurse_id || null,
            second_physio_id: a?.physio_id || null,
            second_rate: a?.rate ?? null,
            second_monthly_scale: a?.monthly_scale ?? null,
            second_total_charge: a?.client_charge ?? null,
            second_in_time: a?.in_time || null,
            second_out_time: a?.out_time || null,
            is_overridden: false,
          };
        });
      });

      // --- FINAL CLEANUP (empty slots become "No Duty") ---
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      for (let dd = 1; dd <= daysInMonth; dd++) {
        const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
        if (!newSchedule[dateStr]) newSchedule[dateStr] = {};
        finalShifts.forEach((shift) => {
          if (!newSchedule[dateStr][shift.type]) {
            newSchedule[dateStr][shift.type] = { nurse: 'No Duty', is_no_duty: true, nurse_id: null, assignment_id: null, hasAssistant: false };
          }
        });
      }

      setScheduleMap(newSchedule);
    } catch (e) {
      console.error("Fetch error", e);
    } finally {
      setIsFetching(false);
    }
  }, [patient, resolveByNurseId, resolveByPhysioId, currentMonth]);

  useEffect(() => { if (isOpen) loadScheduleData(); }, [isOpen, loadScheduleData]);

  const handleDrawerSave = async (formData: any) => {
    setIsSaving(true);
    try {
      const token = localStorage.getItem('accessToken');
      const headers = { 'Authorization': `Bearer ${token}` };

      const primaryNurseId  = formData.nurse_id         ? Number(formData.nurse_id)         : null;
      const primaryPhysioId = formData.physio_id         ? Number(formData.physio_id)        : null;
      const secondNurseId   = formData.second_nurse_id   ? Number(formData.second_nurse_id)  : null;
      const secondPhysioId  = formData.second_physio_id  ? Number(formData.second_physio_id) : null;

      if (!primaryNurseId && !primaryPhysioId) {
        alert('Please select a staff member.');
        setIsSaving(false);
        return;
      }

      // Guard: warn before assigning a nurse who is currently marked unavailable
      // (Emergency Leave / Staff On Sick / Planned Leave / On Temporary Duty) for this day.
      const blockedAll = [primaryNurseId, secondNurseId]
        .filter((id): id is number => !!id && !!blockedStaff && !!blockedStaff[id]);
      // Resigned/Absconded/Uncertainty: hard stop, no override — stop right here.
      const hardBlocked = blockedAll.filter(id => PERMANENT_AVAIL_STATUSES.includes(blockedStaff![id]));
      if (hardBlocked.length > 0) {
        const lines = hardBlocked
          .map((id) => {
            const n = resolveByNurseId(id);
            return `${n?.full_name || n?.name || `Staff #${id}`} — marked ${blockedStaff![id]}`;
          })
          .join('\n');
        setBlockedError(`You can't assign the following staff:\n\n${lines}`);
        setIsSaving(false);
        return;
      }
      // Leave / On Temporary Duty: still just a warning, override allowed.
      const blockedHit = blockedAll.filter(id => !PERMANENT_AVAIL_STATUSES.includes(blockedStaff![id]));
      if (blockedHit.length > 0) {
        const lines = blockedHit
          .map((id) => {
            const n = resolveByNurseId(id);
            return `• ${n?.full_name || n?.name || `Staff #${id}`} — ${blockedStaff![id]}`;
          })
          .join('\n');
        const ok = window.confirm(
          `The following staff are currently marked unavailable:\n\n${lines}\n\n` +
          `Do you still want to assign them?\n\n` +
          `OK — assign them and set their availability to "Available" for this day.\n` +
          `Cancel — don't assign; leave their availability unchanged.`
        );
        if (!ok) { setIsSaving(false); return; }
      }

      const hasAssistant = !!(formData.hasAssistant && (secondNurseId || secondPhysioId));

      // Build one upsert per (slot, role); remove assistant where toggled off.
      const upserts: any[] = [];
      const removeAssistant: SelectedSlot[] = [];

      for (const slot of selectedSlots) {
        const shiftType = slot.session;
        const date = slot.date.split('T')[0];
        const stdHrs = getStandardHours(shiftType) || 8;

        const pHrs = calculateHoursDifference(formData.in_time, formData.out_time);
        const pPay = Math.round(((Number(formData.daily_wage) || 0) / stdHrs) * pHrs);
        upserts.push({
          patient_id: patient?.id, date, shift_type: shiftType, role: 'primary',
          nurse_id: primaryNurseId, physio_id: primaryPhysioId,
          in_time: formData.in_time, out_time: formData.out_time, actual_hours: pHrs,
          rate: pPay,
          monthly_scale: formData.monthly_wage ? Number(formData.monthly_wage) : null,
          client_charge: formData.client_charge ? Number(formData.client_charge) : null,
        });

        if (hasAssistant) {
          const sHrs = calculateHoursDifference(formData.second_in_time, formData.second_out_time);
          const sPay = Math.round(((Number(formData.second_daily_wage) || 0) / stdHrs) * sHrs);
          upserts.push({
            patient_id: patient?.id, date, shift_type: shiftType, role: 'assistant',
            nurse_id: secondNurseId, physio_id: secondPhysioId,
            in_time: formData.second_in_time, out_time: formData.second_out_time, actual_hours: sHrs,
            rate: sPay,
            monthly_scale: formData.second_monthly_wage ? Number(formData.second_monthly_wage) : null,
            client_charge: formData.second_client_charge ? Number(formData.second_client_charge) : null,
          });
        } else {
          removeAssistant.push(slot);
        }
      }

      // One bulk upsert for all primary (+ assistant) rows
      if (upserts.length > 0) {
        // Warn before silently moving a staff member who is already assigned
        // elsewhere at an overlapping time (that other slot would be vacated).
        try {
          const { data: cc } = await axios.post(`${API_BASE_URL}/api/schedule/check-conflicts`, upserts, { headers });
          const conflicts: any[] = cc?.conflicts || [];
          if (conflicts.length > 0) {
            const lines = conflicts
              .map((c) => `• ${c.staff_name} — already assigned to ${c.client_name || c.patient_name} on ${String(c.date).split('-').reverse().join('-')} (${c.shift_type})`)
              .join('\n');
            const ok = window.confirm(
              `${conflicts.length} staff already assigned elsewhere at this time:\n\n${lines}\n\n` +
              `Are you sure you want to move them here? This will vacate the position(s) above.`
            );
            if (!ok) { setIsSaving(false); return; }
          }
        } catch (e) {
          // If the pre-check fails, don't block the save — the backend still
          // enforces one-staff-one-slot on commit.
          console.warn('conflict pre-check failed', e);
        }
        await axios.post(`${API_BASE_URL}/api/schedule/shifts/bulk`, upserts, { headers });
      }
      // Remove any assistant rows on slots where the assistant was toggled off
      for (const slot of removeAssistant) {
        await axios.post(`${API_BASE_URL}/api/schedule/remove-role`, {
          patient_id: patient?.id, date: slot.date.split('T')[0],
          shift_type: slot.session, role: 'assistant',
        }, { headers });
      }

      // Confirmed override: flip the now-assigned unavailable staff to available.
      if (blockedHit.length > 0 && onOverrideAvailability) onOverrideAvailability(blockedHit);

      setSelectedSlots([]);
      loadScheduleData();
    } catch (err) {
      console.error(err);
      alert("Failed to save schedule.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDrawerDelete = async () => {
    if (!window.confirm(`Permanently delete ${selectedSlots.length} selected slot(s)? This cannot be undone.`)) return;
    setIsSaving(true);
    try {
      const token = localStorage.getItem('accessToken');
      const headers = { 'Authorization': `Bearer ${token}` };
      // Hard delete each day — removes primary + assistant from both layers, no tombstone.
      for (const slot of selectedSlots) {
        await axios.post(`${API_BASE_URL}/api/schedule/delete-day`, {
          patient_id: patient?.id,
          shift_type: slot.session,
          date: slot.date.split('T')[0],
        }, { headers });
      }
      setSelectedSlots([]);
      loadScheduleData();
    } catch (err) { console.error(err); alert("Failed to delete assignments."); }
    finally { setIsSaving(false); }
  };

  const handlePatternApply = async (patternConfig: any, range: { startDate: string; endDate: string }) => {
    setIsSaving(true);
    try {
      const token = localStorage.getItem('accessToken');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Guard: warn before assigning nurses currently marked unavailable for this day.
      const patternNurseIds = Array.from(new Set(
        shifts
          .map((shift) => patternConfig[shift.id])
          .filter((c) => c && c.staff_type !== 'physio' && c.nurse_id)
          .map((c) => Number(c.nurse_id))
      ));
      const blockedPatternAll = patternNurseIds.filter((id) => !!blockedStaff && !!blockedStaff[id]);
      // Resigned/Absconded/Uncertainty: hard stop, no override — stop right here.
      const hardBlockedPattern = blockedPatternAll.filter(id => PERMANENT_AVAIL_STATUSES.includes(blockedStaff![id]));
      if (hardBlockedPattern.length > 0) {
        const lines = hardBlockedPattern
          .map((id) => {
            const n = resolveByNurseId(id);
            return `${n?.full_name || n?.name || `Staff #${id}`} — marked ${blockedStaff![id]}`;
          })
          .join('\n');
        setBlockedError(`You can't assign the following staff:\n\n${lines}`);
        setIsSaving(false);
        return;
      }
      // Leave / On Temporary Duty: still just a warning, override allowed.
      const blockedHit = blockedPatternAll.filter(id => !PERMANENT_AVAIL_STATUSES.includes(blockedStaff![id]));
      if (blockedHit.length > 0) {
        const lines = blockedHit
          .map((id) => {
            const n = resolveByNurseId(id);
            return `• ${n?.full_name || n?.name || `Staff #${id}`} — ${blockedStaff![id]}`;
          })
          .join('\n');
        const ok = window.confirm(
          `The following staff are currently marked unavailable:\n\n${lines}\n\n` +
          `Do you still want to assign them?\n\n` +
          `OK — assign them and set their availability to "Available" for this day.\n` +
          `Cancel — don't assign; leave their availability unchanged.`
        );
        if (!ok) { setIsSaving(false); return; }
      }

      // Expand the range into per-day primary upserts for each configured shift column
      const upserts: any[] = [];
      const [sy, sm, sd] = range.startDate.split('-').map(Number);
      const [ey, em, ed] = range.endDate.split('-').map(Number);
      const startObj = new Date(sy, sm - 1, sd);
      const endObj = new Date(ey, em - 1, ed);

      for (const shift of shifts) {
        const config = patternConfig[shift.id];
        if (!config || !(config.nurse_id || config.physio_id)) continue;

        const inT = config.in_time || '08:00';
        const outT = config.out_time || '20:00';
        const hrs = calculateHoursDifference(inT, outT);
        const isPhysio = config.staff_type === 'physio';

        let d = new Date(startObj);
        while (d <= endObj) {
          const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          upserts.push({
            patient_id: patient?.id, date, shift_type: shift.type, role: 'primary',
            nurse_id:  isPhysio ? null : (config.nurse_id ? Number(config.nurse_id) : null),
            physio_id: isPhysio ? (config.nurse_id ? Number(config.nurse_id) : null) : null,
            in_time: inT, out_time: outT, actual_hours: hrs,
            rate: config.daily_wage ? Number(config.daily_wage) : 0,
            monthly_scale: config.monthly_wage ? Number(config.monthly_wage) : null,
            client_charge: config.client_charge ? Number(config.client_charge) : 0,
          });
          d.setDate(d.getDate() + 1);
        }
      }

      if (upserts.length > 0) {
        await axios.post(`${API_BASE_URL}/api/schedule/shifts/bulk`, upserts, { headers });
      }
      // Confirmed override: flip the now-assigned unavailable staff to available.
      if (blockedHit.length > 0 && onOverrideAvailability) onOverrideAvailability(blockedHit);

      setShowQuickFill(false);
      loadScheduleData();
    } catch (err) { console.error(err); alert("Failed to apply pattern."); }
    finally { setIsSaving(false); }
  };

  if (!isOpen || !patient) return null;

  const styles: { [key: string]: React.CSSProperties } = {
    overlay: { position: 'fixed', inset: 0, backgroundColor: 'white', zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center' },
    modalBox: { width: '100vw', height: '100vh', backgroundColor: 'white', display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' },
    header: { padding: '0.25rem 1rem', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    titleBox: { display: 'flex', alignItems: 'center', gap: '0.5rem' },
    iconWrap: { backgroundColor: '#2563eb', padding: '0.3rem', borderRadius: '0.4rem', color: 'white' },
    titleText: { margin: 0, fontSize: '1rem', fontWeight: 900, color: '#1e293b' },
    subText: { margin: 0, fontSize: '0.6rem', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b' },
    navGroup: { display: 'flex', alignItems: 'center', gap: '0.5rem' },
    btnTool: { display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.3rem 0.75rem', borderRadius: '0.4rem', fontSize: '0.7rem', fontWeight: 900, textTransform: 'uppercase', cursor: 'pointer', border: 'none', transition: 'all 0.2s', backgroundColor: showQuickFill ? '#f59e0b' : '#fffbeb', color: showQuickFill ? 'white' : '#b45309' },
    dateNav: { display: 'flex', alignItems: 'center', backgroundColor: '#f8fafc', padding: '0.15rem', borderRadius: '0.4rem', border: '1px solid #e2e8f0' },
    navBtn: { background: 'none', border: 'none', padding: '0.3rem', cursor: 'pointer', color: '#475569', borderRadius: '0.25rem' },
    dateLabel: { padding: '0 0.75rem', fontSize: '0.7rem', fontWeight: 900, color: '#334155', textTransform: 'uppercase', minWidth: '110px', textAlign: 'center' },
    infoBar: { padding: '0.5rem 1.5rem', backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '1rem', fontSize: '0.65rem', fontWeight: 900, textTransform: 'uppercase', color: '#94a3b8' },
    infoBadge: { display: 'flex', alignItems: 'center', gap: '0.5rem', backgroundColor: 'white', padding: '0.25rem 0.75rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0' }
  };

  return createPortal(
    <div style={styles.overlay}>
      <div style={styles.modalBox}>
        <div style={styles.header}>
          <div style={styles.titleBox}>
            <div style={styles.iconWrap}><Calendar size={16} /></div>
            <div>
              <h2 style={styles.titleText}>{getClientName(patient)}</h2>
              <p style={styles.subText}>Patient: <strong>{patient?.patient_name}</strong> | {monthLabel}</p>
            </div>
          </div>

          <div style={styles.navGroup}>
            {selectedSlots.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', color: '#1d4ed8', fontSize: '0.72rem', fontWeight: 700, padding: '0.3rem 0.75rem', borderRadius: '0.5rem' }}>
                <CheckCircle2 size={12} /> {selectedSlots.length} Slots Selected
              </div>
            )}
            <button
              onClick={() => {
                  window.open(`/casesheet/${patient?.id}?month=${currentMonth.getMonth() + 1}&year=${currentMonth.getFullYear()}`, '_blank','noopener,noreferrer');
              }}
              style={{...styles.btnTool, backgroundColor: '#eff6ff', color: '#1d4ed8'}}
            >
              <FileText size={14} /> View Case Sheet
            </button>
            {/* Schedule note — highlighted when a note exists */}
            <button
              onClick={openNote}
              title={noteVal.trim() ? 'View / edit schedule note' : 'Add a schedule note'}
              style={{ ...styles.btnTool, backgroundColor: noteVal.trim() ? '#f59e0b' : '#fffbeb', color: noteVal.trim() ? 'white' : '#b45309' }}
            >
              <FileText size={14} /> Note{noteVal.trim() ? ' •' : ''}
            </button>
            <button onClick={() => setShowQuickFill(!showQuickFill)} style={styles.btnTool}>
              <Zap size={14} /> Bulk - Fill
            </button>
            <div style={styles.dateNav}>
              <button onClick={() => { setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() - 1))); setSelectedSlots([]); }} style={styles.navBtn}><ChevronLeft size={16} /></button>
              <span style={styles.dateLabel}>{monthLabel}</span>
              <button onClick={() => { setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() + 1))); setSelectedSlots([]); }} style={styles.navBtn}><ChevronRight size={16} /></button>
            </div>
            <button onClick={onSuccess} style={{ ...styles.navBtn, backgroundColor: '#f1f5f9', marginLeft: '0.5rem' }} title="Close & Refresh"><X size={18} /></button>
          </div>
        </div>

        {showQuickFill && (
          <PatternTool
            shifts={shifts}
            nurses={localNurses}
            onApply={handlePatternApply}
            onClose={() => setShowQuickFill(false)}
            onQuickAdd={() => setIsAddNurseModalOpen(true)}
          />
        )}

        <ScheduleGrid
          patientId={patient?.id}
          currentMonth={currentMonth}
          dutyType={patient?.duty_type}
          shifts={shifts} setShifts={setShifts}
          scheduleMap={scheduleMap}
          selectedSlots={selectedSlots} setSelectedSlots={setSelectedSlots}
          isFetching={isFetching}
          flaggedCells={flaggedCells}
          // Deleting a column clears its rows on the server; reload so the grid,
          // the totals and the salary figures all drop them together.
          onAssignmentsDeleted={loadScheduleData}
        />

        <ShiftEditDrawer
          selectedSlots={selectedSlots}
          nurses={localNurses}
          physios={localPhysios}
          dutyType={patient?.duty_type}
          scheduleMap={scheduleMap}
          onClose={() => setSelectedSlots([])}
          onSave={handleDrawerSave}
          onDelete={handleDrawerDelete}
          isSaving={isSaving}
          onQuickAdd={() => patient?.duty_type === 'Physiotherapy' ? setIsAddPhysioModalOpen(true) : setIsAddNurseModalOpen(true)}
          preNurseId={preNurseId}
        />

        <AddNurseModal
          isOpen={isAddNurseModalOpen}
          onClose={() => setIsAddNurseModalOpen(false)}
          onSuccess={() => { setIsAddNurseModalOpen(false); refreshNurses(); }}
        />
        <AddPhysioModal
          isOpen={isAddPhysioModalOpen}
          onClose={() => setIsAddPhysioModalOpen(false)}
          onSuccess={() => { setIsAddPhysioModalOpen(false); refreshPhysios(); }}
        />

        {/* Schedule note modal */}
        {noteOpen && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', zIndex: 6000, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(2px)' }}
            onClick={() => setNoteOpen(false)}>
            <div style={{ background: '#fff', borderRadius: '14px', width: '480px', maxWidth: '92vw', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column' }}
              onClick={e => e.stopPropagation()}>
              <div style={{ padding: '14px 18px', borderBottom: '1px solid #e2e8f0', background: '#fafafa', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>Schedule Note</div>
                  <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>{getClientName(patient) || patient?.patient_name}</div>
                </div>
                <button onClick={() => setNoteOpen(false)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '20px', lineHeight: 1, width: '28px', height: '28px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
              </div>
              <div style={{ padding: '16px 18px' }}>
                <textarea autoFocus value={noteDraft} onChange={e => setNoteDraft(e.target.value)}
                  placeholder="Write a note about this schedule…" rows={7}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', fontFamily: 'inherit', outline: 'none', color: '#0f172a', resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.5 }} />
              </div>
              <div style={{ padding: '12px 18px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <button onClick={() => setNoteOpen(false)}
                  style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: '12px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
                <button onClick={saveNote} disabled={noteSaving}
                  style={{ padding: '8px 18px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg,#3b82f6 0%,#2563eb 100%)', color: '#fff', fontSize: '12px', fontWeight: 700, cursor: noteSaving ? 'default' : 'pointer', fontFamily: 'inherit', opacity: noteSaving ? 0.7 : 1 }}>
                  {noteSaving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Can't-assign dialog — hard stop for Resigned/Absconded/Uncertainty staff.
            A simple in-app pop-up (not the browser's native alert), OK-only, no
            override offered. */}
        {blockedError && (
          <div
            onClick={() => setBlockedError(null)}
            style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.55)', zIndex: 8000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{ background: '#fff', borderRadius: '12px', width: '400px', maxWidth: '90vw', boxShadow: '0 20px 40px rgba(0,0,0,0.25)', padding: '1.5rem' }}
            >
              <div style={{ fontWeight: 700, fontSize: '1.05rem', color: '#991b1b', marginBottom: '0.6rem' }}>Can't Assign</div>
              <div style={{ fontSize: '0.88rem', color: '#334155', marginBottom: '1.4rem', whiteSpace: 'pre-line', lineHeight: 1.5 }}>{blockedError}</div>
              <button
                onClick={() => setBlockedError(null)}
                style={{ width: '100%', padding: '0.65rem', background: '#1e3a8a', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer', fontFamily: 'inherit' }}
              >
                OK
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default AssignNurseModal;
